import type {
  CreateGroupTalkSessionParams,
  GroupTalkNextChoiceDto,
  GroupTalkSessionDetailResponse,
  GroupTalkSessionHistoryItemResponse,
  GroupTalkSessionHistoryListResponse,
  GroupTalkIntakeResponse,
  GroupTalkMessageDto,
  GroupTalkParticipantDto,
  GroupTalkSessionResponse,
  GroupTalkSessionStateDto,
  GroupTalkTurnResponse,
} from "./api";
import {
  getGroupTalkDisplayLabel,
  getGroupTalkDisplayRole,
  findGroupTalkStanceLabel,
  getGroupTalkDisplayMode,
  normalizeGroupTalkMemberId,
  normalizeGroupTalkStanceId,
  type GroupTalkDisplayMode,
  type GroupTalkMemberId,
  type GroupTalkParticipant,
  type GroupTalkStanceId,
  type GroupTalkTemplateId,
} from "./groupTalk";

export type GroupTalkSessionStateView = {
  currentCategory: string;
  currentFocusPoint: string;
  openQuestions: string[];
  decisionStatus: string;
};

export type GroupTalkSessionMemberView = {
  participantId: string;
  memberId: GroupTalkMemberId;
  speakerId: string;
  label: string;
  role: "facilitator" | "expert";
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  facilitator: boolean;
};

export type GroupTalkSessionView = {
  sessionId: string;
  templateId: string | null;
  title: string;
  description: string;
  members: GroupTalkSessionMemberView[];
  agreedNotes: string[];
  sessionState: GroupTalkSessionStateView;
  status: string;
  createdAt: string;
};

export type GroupTalkTranscriptMessage = {
  messageId: string;
  participantId: string | null;
  memberId: GroupTalkMemberId | null;
  speakerId: string;
  speakerName: string;
  speakerType: "user" | "facilitator" | "expert";
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  facilitator: boolean;
  phase: "user" | "thinking" | "intake" | "expert" | "summary" | "facilitator_direct";
  sequence: number | null;
  content: string;
  nextChoices: GroupTalkNextChoiceView[];
  createdAt: string;
};

export type GroupTalkNextChoiceView = {
  choiceId?: string;
  label: string;
  value: string;
  choiceType: string;
  actionIntent?: string;
  sourcePhase?: string;
  sourceMessageId?: string;
  requiresExperts?: boolean;
  isRecommended: boolean;
  requiresInput: boolean;
  inputPrompt: string;
  inputPlaceholder: string;
};

const GROUP_TALK_INPUT_CHOICE_TYPES = new Set(["input", "correction", "restart", "clarification"]);
const GROUP_TALK_ACTION_INTENTS = new Set(["compare_table", "recommend", "organize_points", "draft_plan"]);

export const GROUP_TALK_CHOICE_INPUT_MAX_LENGTH = 800;
export const GROUP_TALK_MESSAGE_MAX_LENGTH = 1000;
export const DEFAULT_GROUP_TALK_CHOICE_INPUT_PROMPT = "追加情報を入力してください";

function ensureSentenceEnding(value: string) {
  return /[。.!！?？]$/.test(value) ? value : `${value}。`;
}

export function buildGroupTalkInputChoiceMessage(choice: GroupTalkNextChoiceView, inputValue: string) {
  const trimmedInput = ensureSentenceEnding(inputValue.trim());

  if (choice.choiceType === "correction") {
    return `前提を修正します。${trimmedInput}この前提で考え直してください。`;
  }

  if (choice.choiceType === "restart") {
    return `議論を整理し直したいです。${trimmedInput}この前提で考えてください。`;
  }

  return `対象について補足します。${trimmedInput}この前提で相談を続けたいです。`;
}

export type GroupTalkTurnView = {
  sessionId: string;
  turnId: string;
  messages: GroupTalkTranscriptMessage[];
  createdAt: string;
  isSatisfied: boolean;
};

export type GroupTalkSessionHistoryItemView = {
  sessionId: string;
  title: string;
  previewText: string;
  updatedAt: string;
  createdAt: string;
  memberSummary: string[];
};

export type GroupTalkIntakeView = {
  sessionId: string;
  turnId: string;
  messages: GroupTalkTranscriptMessage[];
  createdAt: string;
  agreedNotes: string[];
  sessionState: GroupTalkSessionStateView;
  needsFollowUp: boolean;
  followUpQuestion: string;
  continueToken: string | null;
};

function normalizeAgreedNotes(value: GroupTalkSessionResponse["agreed_notes"] | GroupTalkTurnResponse["agreed_notes"]) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value
      .split("\n")
      .map((item) => item.replace(/^[-*]\s*/, "").trim())
      .filter((item) => item.length > 0);
  }
  return [];
}

function normalizeSessionState(value: GroupTalkSessionStateDto | null | undefined): GroupTalkSessionStateView {
  const rawOpenQuestions = value?.open_questions;
  const openQuestions = Array.isArray(rawOpenQuestions)
    ? rawOpenQuestions.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];

  return {
    currentCategory: typeof value?.current_category === "string" ? value.current_category : "",
    currentFocusPoint: typeof value?.current_focus_point === "string" ? value.current_focus_point : "",
    openQuestions,
    decisionStatus: typeof value?.decision_status === "string" ? value.decision_status : "discussing",
  };
}

function normalizeParticipantStance(stance: string | null | undefined): GroupTalkStanceId | null {
  return normalizeGroupTalkStanceId(stance);
}

function normalizeActionIntent(value: unknown) {
  const intent = typeof value === "string" ? value.trim() : "";
  return GROUP_TALK_ACTION_INTENTS.has(intent) ? intent : "";
}

function inferActionIntent(label: string, value: string) {
  const text = `${label} ${value}`;
  if (/比較表|比較\s*表|表で|表に|一覧表|マトリクス|3案|三案|案を比較/u.test(text)) {
    return "compare_table";
  }
  if (/おすすめ|お勧め|推奨|推薦|どれがよい|どれが良い/u.test(text)) {
    return "recommend";
  }
  if (/論点整理|判断軸|観点整理|優先順位|整理して|整理する/u.test(text)) {
    return "organize_points";
  }
  if (/実行ステップ|実施ステップ|計画|プラン|たたき台|ロードマップ/u.test(text)) {
    return "draft_plan";
  }
  return "";
}

function normalizeNextChoices(value: unknown): GroupTalkNextChoiceView[] {
  if (!Array.isArray(value)) return [];

  let recommendedAssigned = false;
  const choices: GroupTalkNextChoiceView[] = [];

  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const candidate = item as GroupTalkNextChoiceDto;
    const label = typeof candidate.label === "string" ? candidate.label.trim() : "";
    const choiceValue = typeof candidate.value === "string" ? candidate.value.trim() : "";
    const rawChoiceType = typeof candidate.choice_type === "string" ? candidate.choice_type.trim() : "";
    if (!label || !choiceValue) continue;
    const actionIntent = normalizeActionIntent(candidate.action_intent) || (rawChoiceType === "direction" ? "" : inferActionIntent(label, choiceValue));
    const choiceType = actionIntent ? "action" : rawChoiceType;

    const isRecommended = candidate.is_recommended === true && !recommendedAssigned;
    if (isRecommended) {
      recommendedAssigned = true;
    }

    const normalizedChoice: GroupTalkNextChoiceView = {
      label,
      value: choiceValue,
      choiceType,
      isRecommended,
      requiresInput: candidate.requires_input === true || GROUP_TALK_INPUT_CHOICE_TYPES.has(choiceType),
      inputPrompt: typeof candidate.input_prompt === "string" ? candidate.input_prompt.trim() : "",
      inputPlaceholder: typeof candidate.input_placeholder === "string" ? candidate.input_placeholder.trim() : "",
    };

    const choiceId = typeof candidate.choice_id === "string" ? candidate.choice_id.trim() : "";
    const sourcePhase = typeof candidate.source_phase === "string" ? candidate.source_phase.trim() : "";
    const sourceMessageId = typeof candidate.source_message_id === "string" ? candidate.source_message_id.trim() : "";
    if (choiceId) normalizedChoice.choiceId = choiceId;
    if (actionIntent) normalizedChoice.actionIntent = actionIntent;
    if (sourcePhase) normalizedChoice.sourcePhase = sourcePhase;
    if (sourceMessageId) normalizedChoice.sourceMessageId = sourceMessageId;
    if (typeof candidate.requires_experts === "boolean") normalizedChoice.requiresExperts = candidate.requires_experts;

    choices.push(normalizedChoice);

    if (choices.length >= 4) break;
  }

  return choices;
}

function findNextChoicesTargetIndex(messages: GroupTalkTranscriptMessage[]) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.facilitator && (message.phase === "summary" || message.phase === "intake" || message.phase === "facilitator_direct")) {
      return index;
    }
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].facilitator) {
      return index;
    }
  }

  return -1;
}

function attachTopLevelNextChoices(
  messages: GroupTalkTranscriptMessage[],
  nextChoices: GroupTalkNextChoiceView[]
) {
  if (nextChoices.length === 0) return messages;

  const targetIndex = findNextChoicesTargetIndex(messages);
  if (targetIndex < 0) return messages;

  return messages.map((message, index) =>
    index === targetIndex && message.nextChoices.length === 0
      ? { ...message, nextChoices }
      : message
  );
}

function normalizeParticipantMemberId(memberId: string | null | undefined): GroupTalkMemberId {
  return normalizeGroupTalkMemberId(memberId);
}

function normalizeParticipantView(
  participant: GroupTalkParticipantDto,
  facilitatorName: string,
  displayMode: GroupTalkDisplayMode = "named"
): GroupTalkSessionMemberView {
  const memberId = normalizeParticipantMemberId(participant.member_id);
  const stanceId = normalizeParticipantStance(participant.stance);
  const stanceLabel = findGroupTalkStanceLabel(stanceId, memberId, displayMode);
  const facilitator = participant.role === "facilitator";

  return {
    participantId: participant.participant_id,
    memberId,
    speakerId: participant.speaker_id,
    label: getGroupTalkDisplayLabel(memberId, facilitatorName, displayMode),
    role: participant.role,
    roleLabel: facilitator ? "ファシリテータ" : getGroupTalkDisplayRole(memberId),
    stanceId,
    stanceLabel,
    facilitator,
  };
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripMessageNoise(raw: string, options: { speakerId: string; speakerName: string }) {
  const normalized = raw.replace(/\r\n/g, "\n").trim();
  if (!normalized) return "";

  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .filter(
      (line) =>
        !/^speaker_id\s*:/i.test(line) &&
        !/^member_id\s*:/i.test(line) &&
        !/^speaker_name\s*:/i.test(line) &&
        !/^phase\s*:/i.test(line) &&
        !/^sequence\s*:/i.test(line)
    );

  let joined = lines.join("\n").trim();
  if (!joined) return "";

  const candidates = [options.speakerId, options.speakerName].filter((value) => value.trim().length > 0);

  for (const candidate of candidates) {
    joined = joined.replace(new RegExp(`^${escapeRegExp(candidate)}\\s*[:：]\\s*`, "i"), "").trim();
  }

  return joined;
}

function normalizeMessagePhase(
  message: GroupTalkMessageDto,
  facilitatorSeenCount: number
): GroupTalkTranscriptMessage["phase"] {
  if (message.speaker_type === "user") return "user";
  if (message.phase === "intake" || message.phase === "expert" || message.phase === "summary" || message.phase === "facilitator_direct") {
    return message.phase;
  }
  if (message.speaker_type === "expert") {
    return "expert";
  }
  return facilitatorSeenCount === 0 ? "intake" : "summary";
}

function normalizeMessageView(
  message: GroupTalkMessageDto,
  phase: GroupTalkTranscriptMessage["phase"],
  createdAt: string,
  membersByParticipantId: Map<string, GroupTalkSessionMemberView>,
  facilitatorName: string,
  displayMode: GroupTalkDisplayMode
): GroupTalkTranscriptMessage {
  const member =
    (message.participant_id ? membersByParticipantId.get(message.participant_id) : undefined) ??
    (message.member_id
      ? normalizeParticipantView(
          {
            participant_id: message.participant_id ?? `participant_${message.member_id}`,
            member_id: message.member_id,
            speaker_id: message.speaker_id,
            display_name: message.speaker_name,
            role: message.speaker_type === "facilitator" ? "facilitator" : "expert",
            stance: null,
          },
          facilitatorName,
          displayMode
        )
      : undefined);

  const speakerName =
    message.speaker_type === "user"
      ? typeof message.speaker_name === "string" && message.speaker_name.trim().length > 0
        ? message.speaker_name
        : "ユーザー"
      : member?.label ?? (typeof message.speaker_name === "string" && message.speaker_name.trim().length > 0
        ? message.speaker_name
        : "参加メンバー");

  return {
    messageId: message.message_id,
    participantId: message.participant_id ?? null,
    memberId: message.member_id ? normalizeParticipantMemberId(message.member_id) : null,
    speakerId: message.speaker_id,
    speakerName,
    speakerType: message.speaker_type,
    roleLabel:
      message.speaker_type === "user"
        ? "ユーザー"
        : member?.roleLabel ?? (message.speaker_type === "facilitator" ? "ファシリテータ" : "参加メンバー"),
    stanceId: member?.stanceId ?? null,
    stanceLabel: member?.stanceLabel ?? null,
    facilitator: message.speaker_type === "facilitator",
    phase,
    sequence: typeof message.sequence === "number" ? message.sequence : null,
    content: stripMessageNoise(message.content, {
      speakerId: message.speaker_id,
      speakerName,
    }),
    nextChoices: normalizeNextChoices(message.next_choices),
    createdAt:
      typeof message.created_at === "string" && message.created_at.trim().length > 0
        ? message.created_at
        : createdAt,
  };
}

function groupHistoryMessagesIntoTurns(
  response: GroupTalkSessionDetailResponse,
  session: GroupTalkSessionView,
  facilitatorName: string,
  displayMode: GroupTalkDisplayMode
) {
  const membersByParticipantId = new Map(session.members.map((member) => [member.participantId, member]));
  const turns = new Map<string, GroupTalkTurnView>();
  let facilitatorSeenCount = 0;
  let fallbackIndex = 0;

  response.messages.forEach((message) => {
    const phase = normalizeMessagePhase(message, facilitatorSeenCount);
    if (message.speaker_type === "facilitator") {
      facilitatorSeenCount += 1;
    }

    const turnId =
      typeof message.turn_id === "string" && message.turn_id.trim().length > 0
        ? message.turn_id
        : `history_turn_${fallbackIndex += 1}`;

    const normalized = normalizeMessageView(
      message,
      phase,
      response.updated_at ?? response.created_at,
      membersByParticipantId,
      facilitatorName,
      displayMode
    );

    const currentTurn = turns.get(turnId);
    if (currentTurn) {
      currentTurn.messages.push(normalized);
      return;
    }

    turns.set(turnId, {
      sessionId: response.session_id,
      turnId,
      messages: [normalized],
      createdAt: normalized.createdAt,
      isSatisfied: false,
    });
  });

  return Array.from(turns.values());
}

export function normalizeGroupTalkSessionView(
  response: GroupTalkSessionResponse,
  facilitatorName: string,
  displayMode: GroupTalkDisplayMode = "named"
): GroupTalkSessionView {
  const members = response.members.map((member) => normalizeParticipantView(member, facilitatorName, displayMode));

  return {
    sessionId: response.session_id,
    templateId: typeof response.template_id === "string" ? response.template_id : null,
    title: response.title,
    description: typeof response.description === "string" ? response.description : "",
    members,
    agreedNotes: normalizeAgreedNotes(response.agreed_notes),
    sessionState: normalizeSessionState(response.session_state),
    status: typeof response.status === "string" ? response.status : "active",
    createdAt: response.created_at,
  };
}

export function normalizeGroupTalkIntakeView(
  response: GroupTalkIntakeResponse,
  session: GroupTalkSessionView,
  facilitatorName: string,
  displayMode: GroupTalkDisplayMode = "named"
): GroupTalkIntakeView {
  const membersByParticipantId = new Map(session.members.map((member) => [member.participantId, member]));
  let facilitatorSeenCount = 0;

  return {
    sessionId: response.session_id,
    turnId: response.turn_id,
    messages: attachTopLevelNextChoices(response.messages.map((message) => {
      const phase = normalizeMessagePhase(message, facilitatorSeenCount);
      if (message.speaker_type === "facilitator") {
        facilitatorSeenCount += 1;
      }
      return normalizeMessageView(message, phase, response.created_at, membersByParticipantId, facilitatorName, displayMode);
    }), normalizeNextChoices(response.next_choices)),
    createdAt: response.created_at,
    agreedNotes: normalizeAgreedNotes(response.agreed_notes),
    sessionState: normalizeSessionState(response.session_state),
    needsFollowUp: Boolean(response.needs_follow_up),
    followUpQuestion:
      typeof response.follow_up_question === "string" ? response.follow_up_question.trim() : "",
    continueToken:
      typeof response.continue_token === "string" && response.continue_token.trim().length > 0
        ? response.continue_token
        : null,
  };
}

export function normalizeGroupTalkTurnView(
  response: GroupTalkTurnResponse,
  session: GroupTalkSessionView,
  facilitatorName: string,
  displayMode: GroupTalkDisplayMode = "named"
): GroupTalkTurnView {
  const membersByParticipantId = new Map(session.members.map((member) => [member.participantId, member]));
  let facilitatorSeenCount = 0;

  return {
    sessionId: response.session_id,
    turnId: response.turn_id,
    messages: attachTopLevelNextChoices(response.messages.map((message) => {
      const phase = normalizeMessagePhase(message, facilitatorSeenCount);
      if (message.speaker_type === "facilitator") {
        facilitatorSeenCount += 1;
      }
      return normalizeMessageView(message, phase, response.created_at, membersByParticipantId, facilitatorName, displayMode);
    }), normalizeNextChoices(response.next_choices)),
    createdAt: response.created_at,
    isSatisfied: Boolean(response.is_satisfied),
  };
}

function formatSessionDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function normalizeGroupTalkSessionHistoryList(
  response: GroupTalkSessionHistoryListResponse
): GroupTalkSessionHistoryItemView[] {
  return response.sessions.map((item: GroupTalkSessionHistoryItemResponse) => ({
    sessionId: item.session_id,
    title:
      typeof item.title === "string" && item.title.trim().length > 0
        ? item.title
        : "会議セッション",
    previewText:
      (typeof item.first_user_message === "string" && item.first_user_message.trim().length > 0
        ? item.first_user_message
        : undefined) ??
      (typeof item.last_message_preview === "string" && item.last_message_preview.trim().length > 0
        ? item.last_message_preview
        : undefined) ??
      "会議を再開します",
    updatedAt: formatSessionDateTime(item.updated_at),
    createdAt:
      typeof item.created_at === "string" && item.created_at.trim().length > 0
        ? item.created_at
        : item.updated_at,
    memberSummary: Array.isArray(item.member_summary)
      ? item.member_summary.filter((member): member is string => typeof member === "string" && member.trim().length > 0)
      : [],
  }));
}

export function normalizeGroupTalkSessionDetailView(
  response: GroupTalkSessionDetailResponse,
  facilitatorName: string,
  displayMode?: GroupTalkDisplayMode
) {
  const resolvedDisplayMode = displayMode ?? getGroupTalkDisplayMode(response.template_id === "custom" ? "custom" : "named");
  const session = normalizeGroupTalkSessionView(response, facilitatorName, resolvedDisplayMode);
  const turns = groupHistoryMessagesIntoTurns(response, session, facilitatorName, resolvedDisplayMode);

  return {
    session,
    turns,
  };
}

function buildSpeakerId(memberId: GroupTalkMemberId, index: number) {
  if (memberId === "mei") return "mei";
  return `${memberId}${index}`;
}

export function buildCreateGroupTalkSessionParams(params: {
  draft: {
    templateId: GroupTalkTemplateId;
    title: string;
    description: string;
    participants: GroupTalkParticipant[];
  };
}): CreateGroupTalkSessionParams {
  const counters: Partial<Record<GroupTalkMemberId, number>> = {};

  return {
    templateId: params.draft.templateId,
    title: params.draft.title,
    description: params.draft.description,
    members: params.draft.participants.map((participant) => {
      const memberId = normalizeGroupTalkMemberId(participant.memberId);
      const stanceId = normalizeGroupTalkStanceId(participant.stanceId);
      const nextCount = (counters[memberId] ?? 0) + 1;
      counters[memberId] = nextCount;

      return {
        participantId: participant.participantId,
        memberId,
        speakerId: buildSpeakerId(memberId, nextCount),
        role: memberId === "mei" ? "facilitator" : "expert",
        stance: memberId === "mei" ? null : stanceId,
      };
    }),
  };
}
